import React, { useState, useContext, useRef, useEffect } from "react"
import ForceVerifyEmail from "../../../pages/ForceVerifyEmail"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"
import { ContactRequests } from "../../../api/app.service"
import { CommonFunctionCtx } from "../../../context/commonFunctionContext"
import { generatePassword } from "../../../utils/generatePassword"
import { isCoachInCommissionTracking } from "../../../utils/commissionTracking"
import { isLocalStorageAvailable } from "../../../utils/isLocalStorageAvailable"
import { LoginFormProvider } from "../login-form/context"
import {
  NotificationRequests,
  ReferralRequests,
  UserAccounts,
  VerifyEmail,
} from "../../../api/app.service"
import { OnSuccessfulAuthCallback } from "../context"
import { User } from "../../../api/auth.service"
import { useSearchParams, useLocation, useNavigate } from "react-router-dom"
import { useToast } from "../../../hooks/useToast"

type View = "account-type" | "email" | "password" | "name" | "phone"
export type AccountType = "member" | "coach" | null

export interface RegisterFormContext {
  accountType: AccountType
  setAccountType: (accountType: AccountType) => void

  email: string
  setEmail: (email: string) => void

  password: string
  setPassword: (password: string) => void

  firstName: string
  setFirstName: (name: string) => void

  lastName: string
  setLastName: (name: string) => void

  phone: string | null
  setPhone: (phone: string) => void

  view: View
  setView: (view: View) => void

  createAccount: (organization?: string) => Promise<void>

  isTemp: number
  setIsTemp: (temp: number) => void

  setShowOneTimePasswordForm: (val: boolean) => void

  contactCreated: boolean
  setContactCreated: React.Dispatch<React.SetStateAction<boolean>>
}

export const RegisterFormContext = React.createContext<RegisterFormContext>(
  {} as RegisterFormContext
)

export const RegisterFormProvider: React.FC<
  React.PropsWithChildren<{
    defaultEmail: string
    defaultAccountType: AccountType
    defaultPhone: string
    defaultFirstName: string
    defaultLastName: string
    defaultPassword: string
    onSuccess: OnSuccessfulAuthCallback
    discountedOrderTotal?: number
  }>
> = ({
  children,
  defaultEmail,
  defaultAccountType,
  onSuccess,
  defaultPhone,
  defaultFirstName,
  defaultLastName,
  defaultPassword,
  discountedOrderTotal = 0,
}) => {
  const [accountType, setAccountType] = useState<AccountType>(
    defaultAccountType || null
  )
  const [email, setEmail] = useState<string>(defaultEmail || "")
  const [firstName, setFirstName] = useState<string>(defaultFirstName || "")
  const [lastName, setLastName] = useState<string>(defaultLastName || "")
  const [password, setPassword] = useState<string>(defaultPassword || "")
  const [phone, setPhone] = useState<string | null>(defaultPhone || null)
  const [view, setView] = useState<View>(
    defaultAccountType ? "email" : "account-type"
  )

  const [isTemp, setIsTemp] = useState<number>(0)
  const [contactCreated, setContactCreated] = useState(false)
  const [showOneTimePasswordForm, setShowOneTimePasswordForm] = useState(false)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const isIIN = searchParams.get("flow") === "IIN"
  const { getUser } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const pathParts = location.pathname.split("/").filter(Boolean)
  const isCheckoutPath = pathParts.includes("checkout")
  const guestCheckout = isCheckoutPath && discountedOrderTotal !== 0
  const coachDetails = (() => {
    const raw = secureLocalStorage.getItem("coachDetails")
    return typeof raw === "string" ? JSON.parse(raw) : null
  })()
  const navigate = useNavigate()

  const createAccount = async (organization?: string) => {
    try {
      const referralCode = localStorage.getItem("referrerPromoCode")
      const leadDynoReferralID = localStorage.getItem("affiliateCode") || ""
      let email_query = ""
      if (organization === "BBHC") {
        email_query =
          email === "" ? searchParams.get("verification") || "" : email
      }

      const newUser = await User.createUser({
        email: organization === "BBHC" ? email_query : email.toLowerCase(),
        password: password ? password : generatePassword(),
        first_name: firstName,
        last_name: lastName,
        timezone: { value: moment.tz.guess(true) },
        user_type: accountType || "member",
        phone_number: phone,
        organization:
          accountType === "member" || isTemp
            ? null
            : organization || "Independent",
        connection_id: "",
        referral_code: "",
        email_newsletter: false,
        terms_and_conditions: true,
        referral_id: leadDynoReferralID,
        is_temp: isTemp,
      })

      if (isLocalStorageAvailable()) {
        secureLocalStorage.setItem("user", JSON.stringify(newUser))
      }

      if (newUser.profile_type === "member") {
        localStorage.removeItem("referrerPromoCode")
      }

      const createStreamUserPromise =
        isTemp === 1
          ? UserAccounts.createStreamUserPublic({
              coach_profile_id: parseInt(newUser.coach_profile_id),
              member_profile_id: parseInt(newUser.member_profile_id),
              is_coach: false,
              first_name: firstName || email,
              last_name: lastName || "",
            })
          : UserAccounts.createStreamUser({
              coach_profile_id: parseInt(newUser.coach_profile_id),
              member_profile_id: parseInt(newUser.member_profile_id),
              is_coach: newUser.is_coach,
              first_name: firstName || email,
              last_name: lastName || "",
            })

      const createNotificationSettingsPromise =
        isTemp === 1
          ? NotificationRequests.createNotificationSettingsPublic({
              profiles: [
                {
                  id: newUser.member_profile_id,
                  type: "member",
                },
              ],
            })
          : NotificationRequests.createNotificationSettings({
              profiles: [
                ...(newUser.coach_profile_id
                  ? [{ id: newUser.coach_profile_id, type: "coach" }]
                  : []),
                ...(newUser.member_profile_id
                  ? [{ id: newUser.member_profile_id, type: "member" }]
                  : []),
              ],
            })

      if (isIIN) {
        const createReferralOnCreateAccountPromise = () =>
          organization === "Independent" && referralCode
            ? ReferralRequests.createReferralOnCreateAccount({
                user_id: parseInt(newUser.user_id),
                email: email.toLowerCase(),
                promo_code: referralCode,
              })
            : Promise.resolve()

        const setEmailVerifiedPromise = VerifyEmail.confirmEmailLinkRegister({
          user_id: parseInt(newUser.user_id),
        })

        await Promise.all([
          createStreamUserPromise,
          createNotificationSettingsPromise,
          createReferralOnCreateAccountPromise(),
          setEmailVerifiedPromise,
        ])
      } else {
        const promises: Promise<any>[] = [
          createStreamUserPromise,
          createNotificationSettingsPromise,
        ]

        await Promise.all(promises)
      }

      if (isTemp !== 1) {
        const userData = await getUser()
        onSuccess(userData)

        if (isTemp === 2) {
          setShowOneTimePasswordForm(true)
        }

        if (coachDetails && !guestCheckout) {
          try {
            await ContactRequests.createContactConnection({
              sender_profile_id: coachDetails.coach_profile_id,
              receiver_profile_id: userData.activeProfileId,
              user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
              status: userData.isCoach
                ? "c2c"
                : isCoachInCommissionTracking(coachDetails.endpoint_slug)
                ? "zoee lead"
                : "connected",
            })

            secureLocalStorage.removeItem("coachDetails")
          } catch (ex: any) {
            secureLocalStorage.removeItem("coachDetails")
            console.error("Failed to create contact connection:", ex)
          }

          if (isTemp !== 1) {
            const isCoach = newUser.profile_type === "coach"
            const coachRedirect = localStorage.getItem("coachRedirect")
            const memberRedirect = localStorage.getItem("memberRedirect")

            const currentLocation = localStorage.getItem(
              "currentLocation"
            ) as string
            const referrerPromoCode =
              localStorage.getItem("referrerPromoCode") || ""

            const pathname = isCoach
              ? coachRedirect === "/sign-in"
                ? "/coach"
                : currentLocation === "/pricing"
                ? "/coach/subscription/plans"
                : coachRedirect ||
                  currentLocation ||
                  (referrerPromoCode.toUpperCase().includes("ICFMEMBERS")
                    ? "/coach/subscription/plans"
                    : "/coach")
              : memberRedirect === "/sign-in"
              ? "/member"
              : memberRedirect || currentLocation || "/member"
            navigate(pathname)
          }
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message)
    }
  }

  return (
    <RegisterFormContext.Provider
      value={{
        accountType,
        setAccountType,
        email,
        setEmail,
        password,
        setPassword,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phone,
        setPhone,
        view,
        setView,
        createAccount,
        isTemp,
        setIsTemp,
        setShowOneTimePasswordForm,
        contactCreated,
        setContactCreated,
      }}
    >
      {children}
      <LoginFormProvider defaultEmail="" onSuccess={() => true}>
        {showOneTimePasswordForm && (
          <ForceVerifyEmail
            guestCheckout={guestCheckout}
            isTemp={isTemp}
            onVerified={async () => {
              if (coachDetails) {
                try {
                  const userData = await getUser()
                  await ContactRequests.createContactConnection({
                    sender_profile_id: coachDetails.coach_profile_id,
                    receiver_profile_id: userData.activeProfileId,
                    user_environment:
                      process.env.REACT_APP_USER_ENVIRONMENT || "",
                    status: userData.isCoach
                      ? "c2c"
                      : isCoachInCommissionTracking(coachDetails.endpoint_slug)
                      ? "zoee lead"
                      : "connected",
                  })
                  setContactCreated(true)
                  secureLocalStorage.removeItem("coachDetails")
                } catch (ex: any) {
                  secureLocalStorage.removeItem("coachDetails")
                  console.error("Failed to create contact connection:", ex)
                }
              }
            }}
          />
        )}
      </LoginFormProvider>
    </RegisterFormContext.Provider>
  )
}
